<template>
    <div class="my-6">
        <h3 class="font-bold">List of {{ route.name }}</h3>
        <span class="text-sm text-gray-500">From this screen, you could enable or disable the weeks in which you want your
            users to be able to enter time records. Remembering that this control does not overlap the project date.
        </span>
    </div>
    <div class="my-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">Select Year</label>
        <select v-model="selectedYear" @change="loadData" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary">
            <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
        </select>
    </div>
    <div class="flex justify-end mb-2 space-x-2 text-sm">
        <button class="px-3 py-1 border rounded-md hover:bg-gray-100" @click="expandAll">Expand all</button>
        <button class="px-3 py-1 border rounded-md hover:bg-gray-100" @click="collapseAll">Collapse all</button>
    </div>
    <div class="card">
        <div class="w-full overflow-x-auto rounded-lg shadow-xs">
            <table class="w-full whitespace-no-wrap">
                <thead>
                    <tr class="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50">
                        <th v-for="header in headers" :key="header.key" class="px-4 py-4">{{ header.key }}</th>
                    </tr>
                </thead>
                <tbody class="bg-white">
                    <template v-for="group in groups" :key="group.name">
                        <tr class="text-gray-800 bg-gray-100 font-semibold cursor-pointer" @click="toggleMonth(group.name)">
                            <td class="px-4 py-3 text-sm">
                                <span class="inline-block w-4">{{ isExpanded(group.name) ? '▾' : '▸' }}</span>
                                {{ group.name }}
                                <span class="ml-2 text-xs font-normal text-gray-500">({{ group.weeks.length }} weeks)</span>
                            </td>
                            <td class="px-4 py-3 text-sm"></td>
                            <td class="px-4 py-3 text-sm"></td>
                            <td class="px-4 py-3 text-sm">{{ group.month.active }}</td>
                            <td class="px-4 py-3 text-sm" @click.stop>
                                <i title="Change status" class="gg-block cursor-pointer hover:text-primary"
                                    @click="changeStatus(group.month)"></i>
                            </td>
                        </tr>
                        <template v-if="isExpanded(group.name)">
                            <tr v-for="(week, index) in group.weeks" :key="week.id"
                                :class="`text-gray-700 ${index % 2 != 0 ? 'bg-gray-50' : ''}`">
                                <td class="px-4 py-3 text-sm"></td>
                                <td class="px-4 py-3 text-sm">{{ week.weekNumber }}</td>
                                <td class="px-4 py-3 text-sm">{{ week.dateRange }}</td>
                                <td class="px-4 py-3 text-sm">{{ week.active }}</td>
                                <td class="px-4 py-3 text-sm">
                                    <i title="Change status" class="gg-block cursor-pointer hover:text-primary"
                                        @click="changeStatus(week)"></i>
                                </td>
                            </tr>
                        </template>
                    </template>
                </tbody>
            </table>
        </div>
    </div>
</template>
  
<script>
import { computed, inject, onMounted, ref } from "vue";
import { GET, PATCH, POST } from "../../services/api";
import { useRoute, useRouter } from "vue-router";

export default {
    name: "controlers",
    setup() {
        const headers = [
            { key: "Month", value: "month" },
            { key: "Week", value: "weekNumber" },
            { key: "Date Range", value: "dateRange" },
            { key: "Status", value: "active" },
            { key: "Options", value: "" },
        ];

        const route = useRoute();
        const router = useRouter();
        const modal = inject("modal");
        const alert = inject("alert");
        const loader = inject("loading");
        const data = ref([]);
        const selectedYear = ref(new Date().getFullYear());
        const availableYears = ref([]);
        const expandedMonths = ref([]);

        const monthNamesList = ["January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"];

        const groups = computed(() => {
            const result = [];
            let current = null;
            for (const item of data.value) {
                if (item.type === 'month') {
                    current = { name: item.month, month: item, weeks: [] };
                    result.push(current);
                } else if (current) {
                    current.weeks.push(item);
                }
            }
            return result;
        });

        const isExpanded = (name) => expandedMonths.value.includes(name);
        const toggleMonth = (name) => {
            expandedMonths.value = isExpanded(name)
                ? expandedMonths.value.filter(m => m !== name)
                : [...expandedMonths.value, name];
        };
        const expandAll = () => { expandedMonths.value = [...monthNamesList]; };
        const collapseAll = () => { expandedMonths.value = []; };
        const expandCurrentMonth = () => {
            const now = new Date();
            expandedMonths.value = selectedYear.value === now.getFullYear() ? [monthNamesList[now.getMonth()]] : [];
        };

        const getWeeksOfYear = (year) => {
            const weeks = [];
            const firstDayOfYear = new Date(year, 0, 1);
            const lastDayOfYear = new Date(year, 11, 31);

            const currentWeekStart = new Date(firstDayOfYear);
            const dayOfWeek = currentWeekStart.getDay();
            const diff = currentWeekStart.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
            currentWeekStart.setDate(diff);

            let weekNumber = 1;
            const monthNames = ["January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"];

            while (currentWeekStart <= lastDayOfYear) {
                const currentWeekEnd = new Date(currentWeekStart);
                currentWeekEnd.setDate(currentWeekStart.getDate() + 6);

                if (currentWeekEnd.getFullYear() === year) {
                    const month = monthNames[currentWeekStart.getMonth()];
                    const startDate = currentWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                    const endDate = currentWeekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

                    weeks.push({
                        id: weekNumber,
                        weekNumber: weekNumber,
                        dateRange: `${startDate} - ${endDate}`,
                        month: month,
                        startDate: currentWeekStart.toISOString().split('T')[0],
                        endDate: currentWeekEnd.toISOString().split('T')[0],
                        status: true,
                        active: 'active',
                        type: 'week'
                    });
                }

                currentWeekStart.setDate(currentWeekStart.getDate() + 7);
                weekNumber++;
            }

            return weeks;
        };

        const getMonthsOfYear = (year) => {
            const months = [];
            const monthNames = ["January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"];

            for (let i = 0; i < 12; i++) {
                months.push({
                    id: i + 1,
                    name: monthNames[i],
                    month: monthNames[i],
                    month_value: i,
                    status: true,
                    active: 'active',
                    year: year
                });
            }

            return months;
        };

        const loadData = async () => {
            try {
                loader.open();
                const year = selectedYear.value;
                const existingMonths = await GET("controls/months");
                const existingWeeks = await GET("controls/weeks");

                const monthsOfYear = getMonthsOfYear(year);
                const weeksOfYear = getWeeksOfYear(year);

                for (const month of monthsOfYear) {
                    const existingControl = existingMonths.find(el => el.month_value === month.month_value && el.year === year);
                    if (!existingControl) {
                        await POST("controls/month", {
                            name: month.name,
                            month_value: month.month_value,
                            year: year,
                            status: true
                        });
                    }
                }

                for (const week of weeksOfYear) {
                    const existingControl = existingWeeks.find(el => el.weekNumber === week.weekNumber && el.year === year);
                    if (!existingControl) {
                        await POST("controls/week", {
                            weekNumber: week.weekNumber,
                            year: year,
                            startDate: week.startDate,
                            endDate: week.endDate,
                            month: week.month,
                            status: true
                        });
                    }
                }

                const updatedMonths = await GET("controls/months");
                const updatedWeeks = await GET("controls/weeks");

                const monthData = monthsOfYear.map(month => {
                    const existingControl = updatedMonths.find(el => el.month_value === month.month_value && el.year === year);
                    return {
                        ...month,
                        id: existingControl ? existingControl.id : month.id,
                        status: existingControl ? existingControl.status : true,
                        active: (existingControl ? existingControl.status : true) == true ? 'active' : 'desactive',
                        type: 'month'
                    };
                });

                const weekData = weeksOfYear.map(week => {
                    const existingControl = updatedWeeks.find(el => el.weekNumber === week.weekNumber && el.year === year);
                    return {
                        ...week,
                        id: existingControl ? existingControl.id : week.id,
                        status: existingControl ? existingControl.status : true,
                        active: (existingControl ? existingControl.status : true) == true ? 'active' : 'desactive',
                        type: 'week',
                    };
                });

                const monthNames = ["January", "February", "March", "April", "May", "June",
                    "July", "August", "September", "October", "November", "December"];

                const sortedData = [];
                for (let i = 0; i < 12; i++) {
                    const monthName = monthNames[i];
                    const monthItem = monthData.find(m => m.month === monthName);
                    if (monthItem) {
                        sortedData.push(monthItem);
                    }
                    const monthWeeks = weekData.filter(w => w.month === monthName);
                    sortedData.push(...monthWeeks);
                }

                data.value = sortedData;
                expandCurrentMonth();

                loader.close();
            } catch (e) {
                loader.close();
                if (e.includes("Token") || e.includes("t_dynamik") || e == "Unauthorized") {
                    localStorage.removeItem("t_dynamik");
                    localStorage.removeItem("info_dynamik");
                    router.push({ path: '/login' });
                }
                alert.open("Error", e, "danger");
            }
        };

        onMounted(() => {
            const currentYear = new Date().getFullYear();
            availableYears.value = [currentYear - 4,currentYear - 3,currentYear - 2,currentYear - 1, currentYear, currentYear + 1];
            loadData();
        });


        const changeStatus = (item) => {
            try {

                const statusFunction = async function () {
                    try {
                        loader.open();
                        const endpoint = item.type === 'month' ? `controls/month/${item.id}` : `controls/week/${item.id}`;
                        await PATCH(endpoint, { status: !item.status });

                        item.status = !item.status;
                        item.active = item.status ? 'active' : 'desactive';

                        if (item.type === 'month') {
                            // the API cascades the month status to its weeks
                            const updatedWeeks = await GET("controls/weeks");
                            for (const week of data.value) {
                                if (week.type !== 'week') continue;
                                const fresh = updatedWeeks.find(el => el.id === week.id);
                                if (fresh) {
                                    week.status = fresh.status;
                                    week.active = fresh.status ? 'active' : 'desactive';
                                }
                            }
                        }

                        loader.close();
                        const itemType = item.type === 'month' ? 'Month' : 'Week';
                        const itemName = item.type === 'month' ? item.name : item.weekNumber;
                        alert.open("Success!", `${itemType} <b>${itemName}</b> was updated successfully!`, "success");
                    } catch (e) {
                        loader.close();
                        if (e.includes("Token") || e.includes("t_dynamik") || e == "Unauthorized") {
                            localStorage.removeItem("t_dynamik");
                            localStorage.removeItem("info_dynamik");
                            router.push({ path: '/login' });
                        }
                        alert.open("Error", e, "danger");
                    }
                }

                loader.open();
                const itemType = item.type === 'month' ? 'month' : 'week';
                modal.open(
                    "Attention",
                    `Do you really wish to change this ${itemType} status?`,
                    "warning",
                    "Yes",
                    () => statusFunction(),
                    modal.close()
                );
                loader.close();
            } catch (error) {
                loader.close();
            }
        }

        return { headers, data, groups, route, selectedYear, availableYears, loadData, isExpanded, toggleMonth, expandAll, collapseAll, changeStatus };
    },
};
</script>
  
<style></style>